import * as React from 'react';
import styled, { useTheme, DefaultTheme } from 'styled-components';
import Color from 'color';
import { AvatarSizeName, ColorName, getAvatarSize, getColor as getColorTheme } from "--components/theme";
import { usePrevious } from '--client/common/hooks/usePrevious';


export type ProgressLevel = "low" | "normal" | "high" | "critical";
export interface ProgressColorset {
    primary: (theme: DefaultTheme) => string;
    secondary: (theme: DefaultTheme) => string;
};
export interface Breakpoint {
    level: ProgressLevel
    colorScheme: ProgressColorset
}
export type Breakpoints = Map<number, Breakpoint>;


type CircularProgressTBIProps = {
    startTime: Date
    endTime: Date
    percent?: never
    duration?: never
} & CircularProgressBaseProps;
type CircularProgressPercentProps = {
    percent: number            // 0-1 instantaneous percentage
    duration?: number           // Duration to animate pc change over
    startTime?: never
    endTime?: never
} & CircularProgressBaseProps;
export type CircularProgressBaseProps = {
    style?: React.CSSProperties
    className?: string
    size?: AvatarSizeName
    colorScheme?: Breakpoints | ColorName | string
    ringWidth?: number          // Width of the ring around the avatar
    ringOffset?: number         // How far to offset the ring from the avatar
    width?: number
    reverse?: boolean
    rounded?: boolean
    onBreakpointHit?: (breakpoint: string) => void;
}


type CircularProgressProps = CircularProgressTBIProps | CircularProgressPercentProps;


const DEFAULT_COLORSCHEME: Breakpoints = new Map([
    [0, { level: "low", colorScheme: { primary: theme => theme.palette.positive.main, secondary: theme => theme.palette.positive.background } }],
    [0.5, { level: "normal", colorScheme: { primary: theme => theme.palette.positive.main, secondary: theme => theme.palette.positive.background } }],
    [0.75, { level: "high", colorScheme: { primary: theme => theme.palette.warning.main, secondary: theme => theme.palette.warning.background } }],
    [1, { level: "critical", colorScheme: { primary: theme => theme.palette.danger.main, secondary: theme => theme.palette.danger.background } }]
]);

const SVGRing = styled.svg<{ roundCap?: boolean }>`
    transform: rotate(-90deg);
    & circle {
        fill: none;
        transition-property: stroke;
        transition-duration: ${props => props.theme.transitions.long};
    }
    & circle:nth-child(2) {
        stroke-linecap: ${props => props.roundCap ? "round" : "butt"};
    }
`;

const Wrapper = styled.div<{ size: number }>`
    position: relative;
    overflow: hidden;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
`;


const CircularProgress: React.FC<CircularProgressProps> = (props) => {
    const theme = useTheme();
    let breakpoint: string = "";
    const {
        size, style, className, reverse,
        percent, duration = 0,
        endTime, startTime = new Date(),
        colorScheme = DEFAULT_COLORSCHEME,
    } = props;
    const backgroundRing = React.useRef<SVGCircleElement>(null);
    const foregroundRing = React.useRef<SVGCircleElement>(null);
    const frameRef = React.useRef<number>();
    const timerRef = React.useRef<number>();
    const pxSize = getAvatarSize(size);
    const pxRingWidth = pxSize / (pxSize < 24 ? 6 : 8);
    const prevPercent = usePrevious(percent) || 0;
    const rounded = pxSize < 24 ? false : props.rounded;

    const initAnimation = () => {
        stopAnimation();
        doFrame();
        return stopAnimation;
    }
    const stopAnimation = () => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        if (timerRef.current) clearTimeout(timerRef.current);
    }
    const doFrame = () => {
        if (!foregroundRing.current || !backgroundRing.current) return;
        let animationDelay: number = 0,
            position: number = percent || 0;

        // Check for an end time or duration if we have one calculate position
        if (endTime || duration) {
            let calcDuration = endTime ? endTime.getTime() - startTime.getTime() : duration || 0;
            let current = new Date().getTime() - startTime.getTime();
            let animationPercent = current / calcDuration;
            let start = prevPercent || 0;
            let end = typeof percent == "number" ? percent : 1;
            position = start + (end - start) * animationPercent
            position = Math.min(Math.max(position, Math.min(start, end)), Math.max(start, end));
            animationDelay = Math.ceil(calcDuration / 360);
        }

        // Update the 
        let colors = getColor(position);
        let offset = circumference * position;
        let offsetA = (circumference - offset).toString(10)
        let offsetB = (circumference * 2 - offset).toString(10)
        backgroundRing.current.style.stroke = colors.secondary(theme);
        backgroundRing.current.style.strokeDashoffset = reverse ? offsetA : offsetB;
        foregroundRing.current.style.stroke = colors.primary(theme);
        foregroundRing.current.style.strokeDashoffset = reverse ? offsetB : offsetA;

        // Check if we should queue up another frame of animation
        if (position != (percent || 1)) {
            if (animationDelay < 750) {
                frameRef.current = requestAnimationFrame(doFrame);
            } else {
                timerRef.current = window.setTimeout(() => {
                    frameRef.current = requestAnimationFrame(doFrame);
                }, animationDelay)
            }
        }
    }
    const getColor = (current: number): ProgressColorset => {
        // This is hideous... 

        if (typeof colorScheme == "string") {
            return {
                primary: (theme) => getColorTheme(colorScheme, theme) as string,
                secondary: (theme) => Color(getColorTheme(colorScheme, theme)).fade(0.75).toString() as string
            }

        } else {
            let foundKey = 0,
                keys = Array.from(colorScheme.keys());
            for (let i = 0; i < keys.length; i++) {
                let key = keys[i]!;
                if (key <= current) foundKey = key;
                else break;
            }
            let found = colorScheme.get(foundKey)!;
            if (breakpoint !== found.level && props.onBreakpointHit) {
                breakpoint = found.level;
                props.onBreakpointHit(breakpoint);
            }
            return found.colorScheme;
        }
    }


    const radius = pxSize / 2 - pxRingWidth / 2;
    const circumference = 2 * Math.PI * radius;
    const svgCircleProps: React.SVGProps<SVGCircleElement> = {
        cx: pxSize / 2,
        cy: pxSize / 2,
        r: radius,
        strokeWidth: pxRingWidth,
        strokeDasharray: circumference,
    }


    // 1. Trigger restart animation on props change
    React.useEffect(initAnimation, [percent, startTime, endTime, pxSize]);

    return (
        <Wrapper size={pxSize} className={className} style={style}>
            <SVGRing viewBox={`0 0 ${pxSize} ${pxSize}`} xmlns="http://www.w3.org/2000/svg" version="1.1" roundCap={rounded}>
                <circle ref={backgroundRing} {...svgCircleProps}></circle>
                <circle ref={foregroundRing} {...svgCircleProps}></circle>
            </SVGRing>
        </Wrapper>
    );
}

export default CircularProgress;
export { CircularProgress, CircularProgressProps };